import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import StickyBox from 'react-sticky-box';
import translations from '@nike/gameday-translations/web';
import { RadioStyled, RadioSetStyled } from '@nike/nike-design-system-components';

import { filtersTypes, selectedDepartmentTypes } from '../../types/shop';

import { H2, Currency, Button, Icon, Link, Slider } from '../../components';
import Filter from '~COMPONENTS/filters/Filter';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { Facets } from '../facets';
import { Filters } from '../filters';
import { DownloadModalCancelOverlay } from '../../downloads';

import {
  addPriceRange,
  addSelectedFilter,
  clearFacets,
  clearSelectedFilters,
  clearSelectedFacet,
  removeSelectedFilter,
  selectFacet,
  setAllSizesAvailable,
  setIncludeUnavailableProduct,
  storeActiveSubTypes,
  storeAllSubTypes,
  togglePriceRangeSet
} from '../actions';
import {
  FILTER_CATALOGS,
  FILTER_DIMENSIONS,
  FILTER_SIZE,
  ORDER_TYPE_FUTURES,
  ORDER_TYPE_FORECAST,
  ORDER_TYPE_ATONCE
} from '../constants';
import {
  filterCount,
  getFacets,
  getFilters,
  getSelectedDepartment,
  getSelectedFacets,
  getSelectedFilters,
  priceRangeMax,
  priceRangeMin,
  priceRangeSet,
  getProductsRequest,
  selectIncludeUnavailableProduct
} from '../selectors';
import { useCurrentContract } from '../../account/reducer';
import { openModal, closeModal, disableModal, enableModal } from '../../components/modal/actions';
import { setStatus, generateReport } from '../../downloads/actions';

import { STATUS_IDLE, REPORT_TYPE_GRIDWALL } from '../../downloads/constants';
import { getUserRole } from '../../util/languageTranslations';

export class GridwallFilters extends Component {
  handleFilterChange = (name, isChecked) => {
    const {
      addSelectedFilter,
      removeSelectedFilter,
      loadFilteredProducts,
      includeUnavailableProduct,
      setIncludeUnavailableProduct
    } = this.props;
    const [key, value] = name.split('--');

    // if Include Unavailable Products selected and user filters on size,
    // change to All Available Products and reload gridwall
    if (includeUnavailableProduct && key === FILTER_SIZE && isChecked) {
      setIncludeUnavailableProduct(false);
    }

    (isChecked ? addSelectedFilter : removeSelectedFilter)({ key, value });
    loadFilteredProducts();
  };

  handlePriceSliderChange = range => {
    const { addPriceRange, defaultPrice, setWholesalePrice, togglePriceRangeSet, loadFilteredProducts } = this.props;
    const isDefaultWholesaleRange = JSON.stringify(range) === JSON.stringify(defaultPrice);

    setWholesalePrice(range);
    addPriceRange(range);
    togglePriceRangeSet(!isDefaultWholesaleRange);
    loadFilteredProducts();
  };

  handleClearAll = () => {
    const { clearFacets, clearSearch } = this.props;
    clearSearch();
    clearFacets();
    this.handleClearFilters();
  };

  handleClearFilters = async () => {
    const { clearSelectedFilters, handlePriceReset, loadFilteredProducts } = this.props;
    handlePriceReset();
    await clearSelectedFilters();
    return loadFilteredProducts();
  };

  confirmDownload = () => {
    const { generateReport, closeModal, disableModal, getProductsRequest } = this.props;
    generateReport({
      type: REPORT_TYPE_GRIDWALL,
      data: {
        productRequest: getProductsRequest
      }
    });
    closeModal();
    disableModal();
  };

  cancelDownload = () => {
    const { setStatus, enableModal } = this.props;
    setStatus(STATUS_IDLE);
    enableModal();
  };

  handleDownloadClick = () => {
    const { openModal, closeModal } = this.props;
    openModal({
      type: 'alert',
      title: new TranslatedCopy({ id: 'downloads.gridwall.title' }).toString(),
      confirmText: new TranslatedCopy({ id: 'global.download' }).toString(),
      confirmAction: this.confirmDownload,
      cancelText: new TranslatedCopy({ id: 'global.cancel' }).toString(),
      cancelAction: closeModal,
      disabledContent: <DownloadModalCancelOverlay cancelDownload={this.cancelDownload} />
    });
  };

  handleSizeAvailabilityChange = event => {
    const { setAllSizesAvailable, loadFilteredProducts } = this.props;

    setAllSizesAvailable(event?.target?.value === 'all');
    loadFilteredProducts();
  };

  handleIncludeUnavailableProductChange = event => {
    const { setIncludeUnavailableProduct, loadFilteredProducts } = this.props;

    setIncludeUnavailableProduct(event?.target?.value === 'includeUnavailable');
    loadFilteredProducts();
  };

  render() {
    let sizeFilter = null;
    let remainingBalance = null;
    let departmentTypeTranslation = null;
    const { en } = translations;

    const {
      clearSelectedFacet,
      defaultPrice,
      deptFacets,
      facets,
      filterCount,
      filters,
      handleFilterClick,
      isFilterOpen,
      isGreaterChinaSalesOrg,
      sortedProducts,
      selectedDepartment,
      selectedFacets,
      selectedFilters,
      selectFacet,
      useCurrentContract,
      wholesalePrice,
      includeUnavailableProduct
    } = this.props;
    if (selectedDepartment && selectedDepartment.type) {
      const { currentRemaining, futureRemaining, type } = selectedDepartment;
      if (type === ORDER_TYPE_FUTURES) {
        remainingBalance = useCurrentContract ? currentRemaining : futureRemaining;
        departmentTypeTranslation = 'global.order.type.futures';
      } else if (type === ORDER_TYPE_FORECAST) {
        departmentTypeTranslation = 'global.order.type.forecast';
      } else {
        remainingBalance = currentRemaining;
        departmentTypeTranslation = 'global.order.type.atonce';
      }
    }

    const showDownload = !!(
      sortedProducts.length &&
      (Object.keys(selectedFilters).length > 0 || Object.keys(selectedFacets).length > 0)
    );

    const currFilters = filters.reduce((filterArr, filter) => {
      const updatedFilters = filterArr;
      if (filter.key !== FILTER_DIMENSIONS && filter.key !== FILTER_SIZE) {
        if (filter.key === FILTER_CATALOGS) {
          updatedFilters.unshift(filter);
        } else {
          updatedFilters.push(filter);
        }
      } else {
        if (selectedFacets.division === 'FTWR' && filter.key === FILTER_DIMENSIONS) {
          // Show shoe width (aka dimensions) filter only when footwear facet is selected
          updatedFilters.push(filter);
        }
        if (filter.key === FILTER_SIZE) {
          const sizeFilterWithSubText = { ...filter, subText: 'gridwall.filters.size.subText' };
          // Show size values only when division + gender selected
          sizeFilter =
            selectedFacets.division && selectedFacets.gender
              ? sizeFilterWithSubText
              : { ...sizeFilterWithSubText, values: [] };
        }
      }

      return updatedFilters;
    }, []);

    // Push size to the end of the filters
    if (sizeFilter) {
      currFilters.push(sizeFilter);
    }

    const updatedFacets =
      deptFacets && deptFacets.length
        ? facets.map(facet => ({
            key: facet.key,
            values: deptFacets.filter(fac => fac.key === facet.key)[0].values
          }))
        : [];

    const showFilters = updatedFacets.length > 0 && updatedFacets[0].values.length > 0;
    return (
      <>
        <StickyBox offsetTop={128} offsetBottom={0} bottom style={{ height: '100%' }}>
          <div className={classnames('gridwall__filters border-right', { isOpen: isFilterOpen })}>
            <Link
              to='/shop/department-select'
              data-desc='gridwall__select_catalog_link'
              className='flex-container row center space-between border-bottom'
            >
              <div className='gridwall__filters_header p6-sm'>
                <H2>
                  <span data-desc='gridwall__left_sidebar_department_name'>
                    {getUserRole() && en[`shop.deptSelect.deptName.${selectedDepartment.number}`]
                      ? `${selectedDepartment.name} 
                       ${en[`shop.deptSelect.deptName.${selectedDepartment.number}`]}`
                      : selectedDepartment.name}
                  </span>
                  <br />
                  <span data-desc='gridwall__left_sidebar_remaining_type'>
                    <TranslatedCopy id={departmentTypeTranslation} />
                  </span>
                </H2>
                {selectedDepartment.type !== ORDER_TYPE_FORECAST && (
                  <div className='text-color-secondary body-4' data-desc='gridwall__remaining_balance'>
                    <TranslatedCopy id='gridwall.balance' />
                    <> </>
                    {isGreaterChinaSalesOrg && <br />}
                    <Currency value={remainingBalance} type={selectedDepartment.code} />
                  </div>
                )}
              </div>
              <div className='pr2-sm'>
                <Icon name='chevron-right-cell' alt='right' />
              </div>
            </Link>
            {Object.keys(selectedFacets).length || filterCount > 0 ? (
              <div style={{ margin: '10px', height: '20px' }}>
                <Button
                  className='text-color-secondary body-4 text-underline p0-sm fl-sm-r'
                  data-desc='gridwall__facet_clear'
                  useBareClass
                  textLink
                  onClick={this.handleClearAll}
                >
                  <TranslatedCopy id='gridwall.search.clear' />
                </Button>
              </div>
            ) : null}
            <Facets
              facets={updatedFacets}
              selected={selectedFacets}
              selectFacet={selectFacet}
              clearSelectedFacet={clearSelectedFacet}
            />
            {showFilters && (
              <>
                <Filters
                  filters={currFilters}
                  handleSizeAvailabilityChange={this.handleSizeAvailabilityChange}
                  onChange={this.handleFilterChange}
                  pageName='gridwall'
                  productCount={sortedProducts.length}
                  selected={selectedFilters}
                  selectedDepartmentType={selectedDepartment.type}
                />
                {defaultPrice && (
                  <Filter trigger='global.product.wholesale.price'>
                    <div>
                      <Currency value={wholesalePrice[0].toString()} type={selectedDepartment.code} />
                      {' - '}
                      <Currency value={wholesalePrice[1].toString()} type={selectedDepartment.code} />
                    </div>
                    <Slider
                      isRange
                      min={defaultPrice[0]}
                      max={defaultPrice[1]}
                      defaultValue={[defaultPrice[0], defaultPrice[1]]}
                      value={[wholesalePrice[0], wholesalePrice[1]]}
                      step={10}
                      onChange={this.handlePriceSliderChange}
                    />
                  </Filter>
                )}
                {selectedDepartment.type === ORDER_TYPE_ATONCE && (
                  <Filter trigger='global.availability' newLabel>
                    <RadioSetStyled
                      legend='Availability'
                      name='availability'
                      onChange={this.handleIncludeUnavailableProductChange}
                      selectedValue={includeUnavailableProduct ? 'includeUnavailable' : 'available'}
                      classes={{
                        fieldSet: 'availableProductFilterContainer',
                        childrenContainer: 'availableProductFilterGroup'
                      }}
                    >
                      <RadioStyled
                        id='allAvailableProducts'
                        label={new TranslatedCopy({
                          id: 'gridwall.filters.availability.allAvailableProducts'
                        }).toString()}
                        value='available'
                        classes={{ label: 'availableProductFilterLabel' }}
                      />
                      <RadioStyled
                        id='includeUnavailableProducts'
                        label={new TranslatedCopy({
                          id: 'gridwall.filters.availability.includeUnavailableProducts'
                        }).toString()}
                        value='includeUnavailable'
                        classes={{ label: 'availableProductFilterLabel' }}
                        disabled={selectedFilters?.size}
                      />
                    </RadioSetStyled>
                  </Filter>
                )}
              </>
            )}
            <div className='p2-sm'>
              <Button secondary extraClassName='full-width d-lg-h' onClick={handleFilterClick}>
                <TranslatedCopy id='global.apply' />
              </Button>
            </div>
            {showDownload && (
              <div className='p2-sm'>
                <Button extraClassName='ncss-btn-primary-dark full-width' onClick={this.handleDownloadClick}>
                  <TranslatedCopy id='global.download' />
                </Button>
              </div>
            )}
          </div>
          <div className='gridwall__filters_bg' onClick={handleFilterClick} />
        </StickyBox>
      </>
    );
  }
}

GridwallFilters.propTypes = {
  addPriceRange: PropTypes.func.isRequired,
  addSelectedFilter: PropTypes.func.isRequired,
  clearFacets: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  clearSelectedFacet: PropTypes.func.isRequired,
  clearSelectedFilters: PropTypes.func.isRequired,
  defaultPrice: PropTypes.arrayOf(PropTypes.number).isRequired,
  deptFacets: PropTypes.arrayOf(PropTypes.object),
  facets: PropTypes.arrayOf(PropTypes.object),
  filterCount: PropTypes.number,
  filters: PropTypes.arrayOf(PropTypes.shape(filtersTypes)),
  handleFilterClick: PropTypes.func.isRequired,
  handlePriceReset: PropTypes.func.isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  loadFilteredProducts: PropTypes.func.isRequired,
  sortedProducts: PropTypes.array,
  removeSelectedFilter: PropTypes.func.isRequired,
  selectedDepartment: selectedDepartmentTypes,
  selectedFacets: PropTypes.object.isRequired,
  selectedFilters: PropTypes.any.isRequired,
  selectFacet: PropTypes.func.isRequired,
  setAllSizesAvailable: PropTypes.func.isRequired,
  setWholesalePrice: PropTypes.func.isRequired,
  setIncludeUnavailableProduct: PropTypes.func.isRequired,
  includeUnavailableProduct: PropTypes.bool.isRequired,
  togglePriceRangeSet: PropTypes.func.isRequired,
  useCurrentContract: PropTypes.bool.isRequired,
  wholesalePrice: PropTypes.arrayOf(PropTypes.number).isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  disableModal: PropTypes.func.isRequired,
  enableModal: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
  generateReport: PropTypes.func.isRequired,
  getProductsRequest: PropTypes.string
};

GridwallFilters.defaultProps = {
  facets: [],
  filterCount: null,
  filters: [],
  selectedDepartment: null,
  deptFacets: [],
  sortedProducts: [],
  getProductsRequest: null
};

const mapStateToProps = state => ({
  facets: getFacets(state),
  filterCount: filterCount(state),
  filters: getFilters(state),
  priceRangeMax: priceRangeMax(state),
  priceRangeMin: priceRangeMin(state),
  priceRangeSet: priceRangeSet(state),
  selectedDepartment: getSelectedDepartment(state),
  selectedFacets: getSelectedFacets(state),
  selectedFilters: getSelectedFilters(state),
  useCurrentContract: useCurrentContract(state),
  getProductsRequest: getProductsRequest(state),
  includeUnavailableProduct: selectIncludeUnavailableProduct(state)
});

const connector = connect(mapStateToProps, {
  addPriceRange,
  addSelectedFilter,
  clearFacets,
  clearSelectedFacet,
  clearSelectedFilters,
  removeSelectedFilter,
  selectFacet,
  setAllSizesAvailable,
  setIncludeUnavailableProduct,
  storeActiveSubTypes,
  storeAllSubTypes,
  togglePriceRangeSet,
  openModal,
  closeModal,
  disableModal,
  enableModal,
  setStatus,
  generateReport
});

export default connector(GridwallFilters);
